/**
 * POIPopup - Popup for Points of Interest (fishing access, boat ramps, etc.) on the map.
 */
import { X, Navigation, Eye } from 'lucide-react';

// Helper to check if a string contains HTML
function containsHTML(s) {
  return /<[a-z][\s\S]*>/i.test(str(s));
}
function str(s) { return typeof s === 'string' ? s : ''; }

export default function POIPopup({
  poi,
  poiTypeColors,
  sanitizeHTML,
  onClose,
}) {
  const typeName = poi.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const hasProps = poi.properties && Object.keys(poi.properties).filter(k => !['destination_id', 'destination_name'].includes(k)).length > 0;
  const filteredProps = hasProps
    ? Object.entries(poi.properties).filter(([key]) => !['destination_id', 'destination_name'].includes(key))
    : [];

  return (
    <div className="relative p-3 min-w-[260px] max-w-[320px]" data-testid="poi-popup">
      <button
        onClick={onClose}
        className="absolute top-2 right-2 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors z-10"
        data-testid="poi-popup-close"
      >
        <X className="w-4 h-4" />
      </button>

      <h3 className="font-serif text-lg font-medium leading-tight mb-1 pr-8" data-testid="poi-popup-name">
        {poi.name}
      </h3>

      <div className="flex items-center gap-2 mb-2">
        <span
          className="inline-block px-2 py-0.5 text-xs rounded-full font-medium text-white"
          style={{ backgroundColor: poiTypeColors[poi.type] || '#3B82F6' }}
          data-testid="poi-popup-type"
        >
          {typeName}
        </span>
        {poi.source && poi.source !== 'manual' && (
          <span className="text-xs text-gray-400">via {poi.source.toUpperCase()}</span>
        )}
      </div>

      {poi.state && (
        <p className="text-xs text-gray-500 mb-2">
          {poi.county ? `${poi.county}, ` : ''}{poi.state}
        </p>
      )}

      {poi.description && (
        containsHTML(poi.description) ? (
          <div
            className="text-sm text-gray-600 mb-3 prose prose-sm max-w-none [&_a]:text-accent [&_a]:underline max-h-32 overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(poi.description) }}
          />
        ) : (
          <p className="text-sm text-gray-600 mb-3 max-h-32 overflow-y-auto">
            {poi.description}
          </p>
        )
      )}

      {hasProps && (
        <div className="bg-gray-50 rounded-lg p-2 mb-3 text-xs space-y-1">
          {filteredProps
            .map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                <span className="font-medium text-gray-700">
                  {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                </span>
              </div>
            ))}
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => {
            window.open(
              `https://www.google.com/maps/dir/?api=1&destination=${poi.latitude},${poi.longitude}`,
              '_blank'
            );
          }}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors"
          data-testid="poi-directions-btn"
        >
          <Navigation className="w-4 h-4" />
          Directions
        </button>
        <button
          onClick={() => {
            window.open(
              `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${poi.latitude},${poi.longitude}`,
              '_blank'
            );
          }}
          className="flex items-center justify-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
          data-testid="poi-streetview-btn"
          title="Open Street View"
        >
          <Eye className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
